import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    IconButton,
    Stack,
    useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    collectionPointsService,
    EventPropagationItem,
    EventPropagationTarget,
    EventPropagationEventType,
} from '../../services/collectionpoints-service';

const EVENT_TYPES: { value: EventPropagationEventType; label: string }[] = [
    { value: 'CONSENT_RECEIVED', label: 'Consent received' },
    { value: 'CONSENT_WITHDRAWN', label: 'Consent withdrawn' },
    { value: 'RETENTION_EXPIRED', label: 'Retention expired' },
    { value: 'ERASURE_REQUESTED', label: 'Erasure requested' },
];

const DEFAULT_TARGET: EventPropagationTarget = {
    url: '',
    method: 'POST',
    active: true,
};

type TargetWithHeadersStr = Omit<EventPropagationTarget, 'headers'> & { headersJson: string };
type EventPropagationFormItem = { eventType: EventPropagationEventType; targets: TargetWithHeadersStr[] };

function targetToForm(t: EventPropagationTarget): TargetWithHeadersStr {
    return {
        url: t.url,
        method: t.method ?? 'POST',
        active: t.active !== false,
        headersJson: t.headers && typeof t.headers === 'object' ? JSON.stringify(t.headers, null, 2) : '',
    };
}
function formToTarget(f: TargetWithHeadersStr): EventPropagationTarget {
    const headers =
        f.headersJson.trim() === ''
            ? undefined
            : (() => {
                  try {
                      return JSON.parse(f.headersJson) as Record<string, string>;
                  } catch {
                      return undefined;
                  }
              })();
    return { url: f.url, method: f.method, active: f.active, headers };
}

interface EventsTabProps {
    collectionPoint: {
        CollectionPointId: number;
        EnableCollectionPointEvents?: boolean;
    } | null;
    onEventsChanged?: () => void;
}

const EventsTab: React.FC<EventsTabProps> = ({ collectionPoint, onEventsChanged }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [propagations, setPropagations] = useState<EventPropagationFormItem[]>([]);

    const enabled = !!collectionPoint?.EnableCollectionPointEvents;

    const loadPropagations = useCallback(async () => {
        if (!collectionPoint?.CollectionPointId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await collectionPointsService.getEventPropagation(collectionPoint.CollectionPointId);
            const byType = new Map(data.map((p) => [p.eventType, p.targets]));
            const full: EventPropagationFormItem[] = EVENT_TYPES.map(({ value }) => ({
                eventType: value,
                targets: (byType.get(value) ?? []).map(targetToForm),
            }));
            setPropagations(full);
        } catch (e: any) {
            setError(e?.message || 'Failed to load event propagation');
            setPropagations(EVENT_TYPES.map(({ value }) => ({ eventType: value, targets: [] as TargetWithHeadersStr[] })));
        } finally {
            setLoading(false);
        }
    }, [collectionPoint?.CollectionPointId]);

    useEffect(() => {
        if (collectionPoint?.CollectionPointId && enabled) {
            loadPropagations();
        } else if (collectionPoint?.CollectionPointId && !enabled) {
            setPropagations(EVENT_TYPES.map(({ value }) => ({ eventType: value, targets: [] as TargetWithHeadersStr[] })));
            setError(null);
        }
    }, [collectionPoint?.CollectionPointId, enabled, loadPropagations]);

    const addTarget = (eventType: EventPropagationEventType) => {
        setPropagations((prev) =>
            prev.map((p) =>
                p.eventType === eventType
                    ? { ...p, targets: [...p.targets, targetToForm(DEFAULT_TARGET)] }
                    : p
            )
        );
    };

    const removeTarget = (eventType: EventPropagationEventType, index: number) => {
        setPropagations((prev) =>
            prev.map((p) =>
                p.eventType === eventType
                    ? { ...p, targets: p.targets.filter((_, i) => i !== index) }
                    : p
            )
        );
    };

    const updateOneTarget = (
        eventType: EventPropagationEventType,
        index: number,
        field: keyof TargetWithHeadersStr,
        value: string | boolean | undefined
    ) => {
        setPropagations((prev) =>
            prev.map((p) => {
                if (p.eventType !== eventType) return p;
                const next = [...p.targets];
                next[index] = { ...next[index], [field]: value };
                return { ...p, targets: next };
            })
        );
    };

    const handleSave = async () => {
        if (!collectionPoint?.CollectionPointId) return;
        setSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const payload: EventPropagationItem[] = propagations.map((p) => ({
                eventType: p.eventType,
                targets: p.targets.map(formToTarget),
            }));
            await collectionPointsService.saveEventPropagation(collectionPoint.CollectionPointId, payload);
            setSuccessMessage('Event propagation saved.');
            onEventsChanged?.();
        } catch (e: any) {
            setError(e?.message || 'Failed to save event propagation');
        } finally {
            setSaving(false);
        }
    };

    if (!collectionPoint) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">Select a collection point.</Alert>
            </Box>
        );
    }

    if (!enabled) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">
                    Enable &quot;Collection point events&quot; in the <strong>Details</strong> tab to configure
                    event propagation (webhooks) for this collection point.
                </Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                When an event occurs, Privigo will send a POST (or configured method) to each active URL below.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            )}

            {EVENT_TYPES.map(({ value, label }) => {
                const item = propagations.find((p) => p.eventType === value);
                const targets = item?.targets ?? [];
                return (
                    <Accordion key={value} defaultExpanded={targets.length > 0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={500}>
                                {label}
                                {targets.length > 0 && (
                                    <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                                        ({targets.length} URL{targets.length !== 1 ? 's' : ''})
                                    </Typography>
                                )}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={2}>
                                {targets.map((t, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            p: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 1,
                                            bgcolor: theme.palette.action.hover,
                                        }}
                                    >
                                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                            <Stack spacing={1.5} sx={{ flex: 1 }}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label="URL"
                                                    value={t.url}
                                                    onChange={(e) =>
                                                        updateOneTarget(value, idx, 'url', e.target.value as string)
                                                    }
                                                    placeholder="https://example.com/webhook"
                                                />
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                                        <InputLabel>Method</InputLabel>
                                                        <Select
                                                            value={t.method || 'POST'}
                                                            label="Method"
                                                            onChange={(e) =>
                                                                updateOneTarget(
                                                                    value,
                                                                    idx,
                                                                    'method',
                                                                    e.target.value as string
                                                                )
                                                            }
                                                        >
                                                            <MenuItem value="POST">POST</MenuItem>
                                                            <MenuItem value="GET">GET</MenuItem>
                                                            <MenuItem value="PUT">PUT</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={t.active !== false}
                                                                onChange={(e) =>
                                                                    updateOneTarget(value, idx, 'active', e.target.checked)
                                                                }
                                                            />
                                                        }
                                                        label="Active"
                                                    />
                                                </Stack>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label="Headers (optional JSON)"
                                                    value={t.headersJson}
                                                    onChange={(e) =>
                                                        updateOneTarget(value, idx, 'headersJson', e.target.value)
                                                    }
                                                    placeholder='{ "X-Api-Key": "..." }'
                                                    multiline
                                                    minRows={1}
                                                    maxRows={3}
                                                    helperText="Valid JSON object. Leave empty if not needed."
                                                />
                                            </Stack>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeTarget(value, idx)}
                                                aria-label="Remove URL"
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                ))}
                                <Button
                                    startIcon={<AddIcon />}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => addTarget(value)}
                                >
                                    Add URL
                                </Button>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : 'Save event propagation'}
                </Button>
            </Box>
        </Box>
    );
};

export default EventsTab;
